import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";
import { radius } from "src/design/tokens";
import { usePermissions } from "src/grading/hooks/usePermissions";
import {
  ROSTERING_ACCESS,
  permissions,
} from "src/grading/utils/permissionsHelpers";

interface DistrictTabItem {
  label: string;
  to: string;
  /** When set, the tab is shown only if the user holds this permission (or any one of a list). */
  permission?:
    | { action: string; subject: string }
    | readonly { action: string; subject: string }[];
}

export interface DistrictTabsProps {
  districtId: string;
}

/**
 * District section navigation (design `dTabs`): a surface pill tray with a shadow; the current
 * section is the strong pill. Each tab is a router link.
 */
export const DistrictTabs = ({ districtId }: DistrictTabsProps) => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const { hasPermissions } = usePermissions();

  const overviewPath = `/grading/districts/${districtId}`;
  const items: DistrictTabItem[] = [
    {
      label: t("district.overview", { defaultValue: "Overview" }),
      to: overviewPath,
    },
    {
      label: t("nav.rostering", { defaultValue: "Rostering" }),
      to: `${overviewPath}/rostering`,
      permission: ROSTERING_ACCESS,
    },
    {
      label: t("district.privacyTab", { defaultValue: "Privacy" }),
      to: `${overviewPath}/privacy`,
      permission: permissions.MANAGE_PRIVACY,
    },
    {
      label: t("district.dataRequests", { defaultValue: "Data requests" }),
      to: `${overviewPath}/data-requests`,
      permission: permissions.MANAGE_DATA_REQUESTS,
    },
  ].filter(
    (item) =>
      !item.permission ||
      (Array.isArray(item.permission)
        ? item.permission
        : [item.permission]
      ).some((p) => hasPermissions(p.action, p.subject)),
  );

  const isCurrent = (to: string) =>
    pathname === to || (to !== overviewPath && pathname.startsWith(`${to}/`));
  const current = items.find((item) => isCurrent(item.to));

  return (
    <Tabs
      value={current ? current.to : false}
      aria-label={t("district.tabs", { defaultValue: "District sections" })}
      variant="scrollable"
      scrollButtons={false}
      TabIndicatorProps={{ sx: { display: "none" } }}
      sx={(theme) => ({
        minHeight: 0,
        p: "4px",
        borderRadius: `${radius.pill}px`,
        bgcolor: theme.palette.background.paper,
        boxShadow: theme.shadows[1],
        maxWidth: "100%",
        "& .MuiTabs-flexContainer": { gap: "4px" },
      })}
    >
      {items.map((item) => (
        <Tab
          key={item.to}
          component={Link}
          to={item.to}
          value={item.to}
          label={item.label}
          aria-current={isCurrent(item.to) ? "page" : undefined}
          disableRipple
          sx={(theme) => ({
            minHeight: 36,
            minWidth: 0,
            px: 2,
            py: 0,
            borderRadius: `${radius.pill}px`,
            textTransform: "none",
            fontSize: 13,
            fontWeight: 700,
            color: theme.palette.text.primary,
            "&.Mui-selected": {
              backgroundColor: theme.palette.strong,
              color: theme.palette.onStrong,
              fontWeight: 800,
            },
            "&.Mui-focusVisible": {
              outline: `2px solid ${theme.palette.focusRing}`,
              outlineOffset: 2,
            },
          })}
        />
      ))}
    </Tabs>
  );
};

export default DistrictTabs;
